/** Per-widget drawer: geometry, visibility, opacity, text scale, type options. */
import { useTranslation } from "react-i18next";
import type {
  BroadcastAsset,
  BroadcastLayout,
  BroadcastWidget,
} from "../../lib/broadcastApi";
import { AnimEditor, PanelEditor } from "./WidgetStyleEditors";
import { clampRect } from "./editorMath";
import { STAT_ROW_KEYS } from "./widgets/ChartWidgets";

interface Props {
  layout: BroadcastLayout;
  widget: BroadcastWidget | null;
  update: (mutate: (draft: BroadcastLayout) => void) => void;
  /** The broadcaster's image library (image widget / panel pickers). */
  library?: BroadcastAsset[];
}

const field =
  "w-full rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-100";

export default function WidgetDrawer({
  layout,
  widget,
  update,
  library = [],
}: Props) {
  const { t } = useTranslation("broadcast");
  if (!widget) {
    return (
      <div
        className="rounded-lg border border-slate-700 p-4 text-sm text-slate-300"
        data-testid="control-widget-drawer-empty"
      >
        {t("control.noSelection")}
      </div>
    );
  }

  const patch = (fn: (w: BroadcastWidget) => void) =>
    update((d) => {
      const target = d.widgets.find((x) => x.id === widget.id);
      if (target) fn(target);
    });

  const setGeometry = (key: "x" | "y" | "w" | "h", raw: string) => {
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    patch((w) => {
      const next = clampRect(
        { x: w.x, y: w.y, w: w.w, h: w.h, [key]: v },
        layout.stage.aspect,
      );
      w.x = next.x;
      w.y = next.y;
      w.w = next.w;
      w.h = next.h;
    });
  };

  const opt = widget.options;
  const setOpt = (key: string, value: unknown) =>
    patch((w) => {
      w.options = { ...w.options, [key]: value };
    });
  const bool = (key: string, fallback: boolean) =>
    typeof opt[key] === "boolean" ? (opt[key] as boolean) : fallback;
  const rows = Array.isArray(opt.rows) ? (opt.rows as string[]) : [];

  return (
    <div
      className="rounded-lg border border-slate-700 p-4 space-y-3"
      data-testid="control-widget-drawer"
      data-widget-id={widget.id}
    >
      <h2 className="text-lg font-semibold">
        {t(`control.widgetTypes.${widget.type}`)}{" "}
        <span className="text-xs font-normal text-slate-400">{widget.id}</span>
      </h2>

      <div className="grid grid-cols-4 gap-2">
        {(["x", "y", "w", "h"] as const).map((k) => (
          <label key={k} className="text-xs text-slate-300">
            {t(`control.geometry.${k}`)}
            <input
              type="number"
              data-testid={`control-widget-${k}`}
              className={field}
              value={widget[k]}
              onChange={(e) => setGeometry(k, e.target.value)}
            />
          </label>
        ))}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          data-testid="control-widget-visible"
          checked={widget.visible}
          onChange={(e) => patch((w) => (w.visible = e.target.checked))}
        />
        {t("control.visible")}
      </label>

      <div>
        <label className="text-xs text-slate-300" htmlFor="wd-opacity">
          {t("control.opacity")}:{" "}
          <span data-testid="control-widget-opacity-value">
            {widget.opacity}
          </span>
        </label>
        <input
          id="wd-opacity"
          type="range"
          min={0.1}
          max={1}
          step={0.05}
          data-testid="control-widget-opacity"
          className="w-full"
          value={widget.opacity}
          onChange={(e) => patch((w) => (w.opacity = Number(e.target.value)))}
        />
      </div>

      <div>
        <label className="text-xs text-slate-300" htmlFor="wd-text">
          {t("control.widgetTextScale")}:{" "}
          <span data-testid="control-widget-text-scale-value">
            {widget.text_scale}
          </span>
        </label>
        <input
          id="wd-text"
          type="range"
          min={0.5}
          max={3}
          step={0.05}
          data-testid="control-widget-text-scale"
          className="w-full"
          value={widget.text_scale}
          onChange={(e) =>
            patch((w) => (w.text_scale = Number(e.target.value)))
          }
        />
      </div>

      {widget.type === "scoreboard" && (
        <div className="space-y-2" data-testid="control-options-scoreboard">
          <label className="block text-xs text-slate-300">
            {t("control.options.variant")}
            <select
              className={field}
              data-testid="control-option-variant"
              value={opt.variant === "compact" ? "compact" : "wide"}
              onChange={(e) => setOpt("variant", e.target.value)}
            >
              <option value="wide">{t("control.options.wide")}</option>
              <option value="compact">{t("control.options.compact")}</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="control-option-show_logos"
              checked={bool("show_logos", true)}
              onChange={(e) => setOpt("show_logos", e.target.checked)}
            />
            {t("control.options.showLogos")}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="control-option-show_clock"
              checked={bool("show_clock", true)}
              onChange={(e) => setOpt("show_clock", e.target.checked)}
            />
            {t("control.options.showClock")}
          </label>
        </div>
      )}

      {widget.type === "live_pitch" && (
        <div className="space-y-2" data-testid="control-options-live_pitch">
          <label className="block text-xs text-slate-300">
            {t("control.options.trailLength")}:{" "}
            {typeof opt.trail_length === "number" ? opt.trail_length : 5}
            <input
              type="range"
              min={0}
              max={10}
              step={1}
              data-testid="control-option-trail_length"
              className="w-full"
              value={
                typeof opt.trail_length === "number" ? opt.trail_length : 5
              }
              onChange={(e) => setOpt("trail_length", Number(e.target.value))}
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="control-option-show_player_labels"
              checked={bool("show_player_labels", false)}
              onChange={(e) => setOpt("show_player_labels", e.target.checked)}
            />
            {t("control.options.showPlayerLabels")}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="control-option-use_team_colors"
              checked={bool("use_team_colors", true)}
              onChange={(e) => setOpt("use_team_colors", e.target.checked)}
            />
            {t("control.options.useTeamColors")}
          </label>
        </div>
      )}

      {widget.type === "stats_panel" && (
        <fieldset
          className="space-y-1"
          data-testid="control-options-stats_panel"
        >
          <legend className="text-xs text-slate-300">
            {t("control.options.rows")}
          </legend>
          <p className="text-xs text-slate-400">
            {t("control.options.rowsHint")}
          </p>
          {STAT_ROW_KEYS.map((key) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid={`control-option-row-${key}`}
                checked={rows.includes(key)}
                onChange={(e) =>
                  setOpt(
                    "rows",
                    e.target.checked
                      ? [...rows, key]
                      : rows.filter((r) => r !== key),
                  )
                }
              />
              {t(`control.statRows.${key}`)}
            </label>
          ))}
        </fieldset>
      )}

      {widget.type === "image" && (
        <div className="space-y-2" data-testid="control-options-image">
          <label className="block text-xs text-slate-300">
            {t("control.options.image")}
            <select
              className={field}
              data-testid="control-option-asset"
              value={typeof opt.asset_id === "string" ? opt.asset_id : ""}
              onChange={(e) => setOpt("asset_id", e.target.value || null)}
            >
              <option value="">{t("control.panel.noImage")}</option>
              {library.map((a) => (
                <option key={a.asset_id} value={a.asset_id}>
                  {t(`control.assets.kinds.${a.kind}`)} {a.width}x{a.height}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-slate-300">
            {t("control.options.fit")}
            <select
              className={field}
              data-testid="control-option-fit"
              value={opt.fit === "cover" ? "cover" : "contain"}
              onChange={(e) => setOpt("fit", e.target.value)}
            >
              <option value="contain">{t("control.options.contain")}</option>
              <option value="cover">{t("control.options.cover")}</option>
            </select>
          </label>
        </div>
      )}

      {["heat_map", "area_control", "shot_map", "top_players"].includes(
        widget.type,
      ) && (
        <label className="block text-xs text-slate-300">
          {t("control.options.team")}
          <select
            className={field}
            data-testid="control-option-team"
            value={
              opt.team === "home" || opt.team === "away" ? opt.team : "both"
            }
            onChange={(e) => setOpt("team", e.target.value)}
          >
            <option value="both">{t("control.options.both")}</option>
            <option value="home">{t("control.options.home")}</option>
            <option value="away">{t("control.options.away")}</option>
          </select>
        </label>
      )}

      <PanelEditor
        panel={widget.panel}
        library={library}
        onChange={(next) => patch((w) => (w.panel = next))}
      />
      <AnimEditor
        which="enter"
        value={widget.enter}
        onChange={(next) => patch((w) => (w.enter = next))}
      />
      <AnimEditor
        which="exit"
        value={widget.exit}
        onChange={(next) => patch((w) => (w.exit = next))}
      />
    </div>
  );
}
